import moment from 'moment'
import { useReselector, useToggle } from '@/utils/sharedHooks'

import {
  Transaction,
  TransactionOrSplit,
} from '@/reducers/admin/allTransactions.slice'
import { Card, CardColors, Text, Tooltip, DocumentUploadDialog } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { DATE_FORMATS } from '@/utils/dateHelpers'
import { EditableTransactionTypeLabel } from '@/components/EditableTransactionTypeLabel'
import { DisplayRowIcon } from './DisplayRowIcon'
import { TransactionCategoryLabel } from '@/components/TransactionCategoryLabel'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { getLockedTransactionDescription } from '../copyConstants'
import {
  selectIsLockedTransaction,
  selectIsSplitTransaction,
} from '../transactions.selectors'

import { UploadDocumentType } from '@/constants/businessConstants'
import { TransactionDetailsMenu } from './TransactionDetailsMenu'
import { selectIsUserManualAccount } from '@/selectors/financeSelectors'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'

const TypeField = ({ transaction }: { transaction: TransactionOrSplit }) => {
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )

  if (isSplitTransaction) {
    return (
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        Split Transaction
      </Text>
    )
  }
  return <EditableTransactionTypeLabel transaction={transaction} />
}

const AmountField = ({ transaction }: { transaction: TransactionOrSplit }) => {
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )
  return (
    <>
      {isSplitTransaction && (
        <s>
          <Text variant={TypographyStyles.BODY_SM}>
            <CurrencyFormatLabel
              value={centsToDollars(transaction.amountInCents)}
            />
          </Text>
        </s>
      )}
      {!isSplitTransaction && (
        <Text variant={TypographyStyles.BODY_SM}>
          <CurrencyFormatLabel
            value={centsToDollars(transaction.amountInCents)}
          />
        </Text>
      )}
    </>
  )
}

/** Used only by SplitTransactionRow — RowContent renders icon, menu, and category inline */
const LastRow = ({
  transaction,
  reports,
}: {
  transaction: TransactionOrSplit
  reports?: boolean
}) => {
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom || transaction.id
  )

  const isManualTransaction = useReselector(
    selectIsUserManualAccount,
    transaction.financialAccountId
  )

  const [fileModalOpen, toggleFileModal] = useToggle(false)

  const { uploadDocuments } = useDocumentUpload({
    documentType: UploadDocumentType.RECEIPT,
    extraPayload: {
      transactionId: transaction.splitFrom || transaction.id,
    },
  })

  return (
    <>
      <div className="flex flex-col sm:flex-row items-center justify-between w-full">
        <div className="flex items-center sm:w-2/12">
          <DisplayRowIcon
            isLocked={locked}
            isAutoCategorized={Boolean(transaction.autocategorizedAt)}
            isManualTransaction={isManualTransaction}
          />
        </div>

        <div className="flex justify-end items-center gap-[20.5px] sm:w-10/12 w-full mt-2 sm:mt-0">
          <TransactionCategoryLabel
            editable={!reports}
            variant="text"
            transaction={transaction}
            filtered
            useRecategorizationModal
          />

          {!reports && !locked && (
            <TransactionDetailsMenu
              transaction={transaction}
              toggleFileModal={toggleFileModal}
              isManualTransaction={isManualTransaction}
            />
          )}
        </div>
      </div>
      <DocumentUploadDialog
        isOpen={fileModalOpen}
        onOpenChange={(open) => !open && toggleFileModal()}
        onClose={toggleFileModal}
        onUpload={(files) => {
          uploadDocuments(files)
        }}
        cardTitle="Upload Receipt"
        cardDescription="Upload a receipt or document for this transaction"
      />
    </>
  )
}

const SplitTransactionRow = ({
  transaction,
  reports,
}: {
  transaction: TransactionOrSplit
  reports?: boolean
}) => {
  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="w-full space-y-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full">
          {!reports && (
            <div className="sm:w-1/2 w-full">
              <TypeField transaction={transaction} />
            </div>
          )}

          <div
            className={`${!reports ? 'sm:w-1/2' : 'w-full'} w-full text-right mt-2 sm:mt-0`}
          >
            <AmountField transaction={transaction} />
          </div>
        </div>

        <LastRow transaction={transaction} reports={reports} />
      </div>
    </Card>
  )
}

const RowContent = ({
  transaction,
  reports,
  hasError,
}: {
  transaction: Transaction
  reports?: boolean
  hasError?: boolean
}) => {
  const isSplitTransaction = useReselector(
    selectIsSplitTransaction,
    transaction.id
  )
  const locked = useReselector(selectIsLockedTransaction, transaction.id)
  const isManualTransaction = useReselector(
    selectIsUserManualAccount,
    transaction.financialAccountId
  )
  const [fileModalOpen, toggleFileModal] = useToggle(false)
  const { uploadDocuments } = useDocumentUpload({
    documentType: UploadDocumentType.RECEIPT,
    extraPayload: {
      transactionId: transaction.id,
    },
  })

  return (
    <div
      className={`w-full flex flex-col border-b border-neutral-300 py-4 ${
        locked && !reports
          ? 'bg-muted opacity-60 pointer-events-none cursor-not-allowed'
          : transaction.requestedClarificationAt
            ? hasError
              ? 'bg-red-25 rounded-lg px-3'
              : 'bg-yellow-25 rounded-lg px-3'
            : ''
      }`}
      {...(locked && !reports ? { 'aria-disabled': 'true' } : {})}
    >
      {/* Date row with icon + action menu aligned right */}
      <div className="flex items-center justify-between pb-2">
        <Text variant={TypographyStyles.BODY_SM}>
          {moment.utc(transaction.date).format(DATE_FORMATS.DISPLAY_SHORT)}
        </Text>
        {!reports && (
          <div className="flex items-center gap-2">
            <DisplayRowIcon
              isLocked={locked}
              isAutoCategorized={Boolean(transaction.autocategorizedAt)}
              isManualTransaction={isManualTransaction}
            />
            {!locked && (
              <TransactionDetailsMenu
                transaction={transaction}
                toggleFileModal={toggleFileModal}
                isManualTransaction={isManualTransaction}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col w-full pb-2">
        <Text variant={TypographyStyles.BODY_SM}>
          <b>{transaction.description}</b>
        </Text>
        {transaction.nickname && (
          <Text variant={TypographyStyles.BODY_SM}>
            <i>{transaction.nickname}</i>
          </Text>
        )}
        <AmountField transaction={transaction} />
      </div>

      {reports && (
        <div className="w-full mt-2">
          <TransactionCategoryLabel
            editable={!reports}
            transaction={transaction}
            filtered
            useRecategorizationModal
          />
        </div>
      )}

      {!reports && (
        <div className={`pb-2 ${hasError ? '[&_button]:!border-red-200' : ''}`}>
          <TypeField transaction={transaction} />
        </div>
      )}

      {!isSplitTransaction && !reports && (
        <div
          className={`flex items-center w-full ${hasError ? '[&_button]:!border-red-200' : ''}`}
        >
          <TransactionCategoryLabel
            editable
            variant="text"
            transaction={transaction}
            filtered
            useRecategorizationModal
          />
        </div>
      )}

      {isSplitTransaction &&
        !reports &&
        transaction.splitTransactions?.map((splitTransaction) => (
          <div key={splitTransaction.id} className="split-transaction-row">
            <SplitTransactionRow
              transaction={splitTransaction}
              reports={reports}
            />
          </div>
        ))}

      <DocumentUploadDialog
        isOpen={fileModalOpen}
        onOpenChange={(open) => !open && toggleFileModal()}
        onClose={toggleFileModal}
        onUpload={(files) => {
          uploadDocuments(files)
        }}
        cardTitle="Upload Receipt"
        cardDescription="Upload a receipt or document for this transaction"
      />
    </div>
  )
}

export const MobileTableRow = ({
  transaction,
  reports,
  hasError,
}: {
  transaction: Transaction
  reports?: boolean
  hasError?: boolean
}) => {
  const locked = useReselector(selectIsLockedTransaction, transaction.id)
  if (locked && !reports) {
    return (
      <div className="flex-grow w-full">
        <Tooltip>
          <Tooltip.Trigger>
            <RowContent transaction={transaction} />
          </Tooltip.Trigger>
          <Tooltip.Content>
            {getLockedTransactionDescription().transactionRowLockIconTooltip}
          </Tooltip.Content>
        </Tooltip>
      </div>
    )
  } else {
    return (
      <RowContent
        transaction={transaction}
        reports={reports}
        hasError={hasError}
      />
    )
  }
}
